import { SystemTypes } from "spark-core-dx/services";
import { deepCopy, normalizeDashboardQueryState } from "./visualQueryStateUtils";

const DESIGN_FIELD_TYPE = {
  MODEL_COLUMN: "ModelColumn",
  MODEL_COLUMNS: "ModelColumns",
  BOOLEAN: "Boolean",
  SELECT: "Select",
  TEXT_AREA: "TextArea",
};

const createClientId = (prefix = "id") =>
  globalThis?.crypto?.randomUUID?.() ?? `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const AGGREGATION_KIND_SUM = 1;
const AGGREGATION_KIND_COUNT = 2;
const AGGREGATION_KIND_COUNT_DISTINCT = 3;
const AGGREGATION_KIND_AVERAGE = 4;
const AGGREGATION_KIND_MIN = 5;
const AGGREGATION_KIND_MAX = 6;
const AGGREGATION_KIND_MEDIAN = 7;
const AGGREGATION_KIND_STD_DEV = 8;
const AGGREGATION_KIND_VARIANCE = 9;
const AGGREGATION_KIND_FIRST = 10;
const AGGREGATION_KIND_LAST = 11;

export const VISUAL_AGGREGATION_OPTIONS = [
  { Text: "Sum", Value: AGGREGATION_KIND_SUM },
  { Text: "Count", Value: AGGREGATION_KIND_COUNT },
  { Text: "Count Distinct", Value: AGGREGATION_KIND_COUNT_DISTINCT },
  { Text: "Average", Value: AGGREGATION_KIND_AVERAGE },
  { Text: "Min", Value: AGGREGATION_KIND_MIN },
  { Text: "Max", Value: AGGREGATION_KIND_MAX },
  { Text: "Median", Value: AGGREGATION_KIND_MEDIAN },
  { Text: "Std Dev", Value: AGGREGATION_KIND_STD_DEV },
  { Text: "Variance", Value: AGGREGATION_KIND_VARIANCE },
  { Text: "First", Value: AGGREGATION_KIND_FIRST },
  { Text: "Last", Value: AGGREGATION_KIND_LAST },
];

const NUMERIC_AGGREGATION_KINDS = new Set([
  AGGREGATION_KIND_SUM,
  AGGREGATION_KIND_AVERAGE,
  AGGREGATION_KIND_MEDIAN,
  AGGREGATION_KIND_STD_DEV,
  AGGREGATION_KIND_VARIANCE,
]);

const ANY_TYPE_AGGREGATION_KINDS = new Set([
  AGGREGATION_KIND_COUNT,
  AGGREGATION_KIND_COUNT_DISTINCT,
]);

const COMPARABLE_AGGREGATION_KINDS = new Set([
  AGGREGATION_KIND_MIN,
  AGGREGATION_KIND_MAX,
  AGGREGATION_KIND_FIRST,
  AGGREGATION_KIND_LAST,
]);

const asArray = (value) => (Array.isArray(value) ? value : []);

const asObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value)
    ? value
    : {};

const normalizeString = (value) => String(value ?? "").trim();

const normalizeMachineToken = (value) =>
  normalizeString(value)
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/[^a-zA-Z0-9_]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase();

const toFriendlyLabel = (value) => {
  const normalized = normalizeString(value);
  if (!normalized) {
    return "";
  }

  return normalized
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (match) => match.toUpperCase());
};

const formatColumnDataTypeName = (value) => {
  const raw = normalizeString(value);
  if (!raw) {
    return "";
  }

  const normalized = raw.split(",")[0].trim();
  const shortName = normalized.includes(".") ? normalized.split(".").pop() : normalized;

  switch (normalizeString(shortName).toLowerCase()) {
    case "string":
      return "string";
    case "boolean":
      return "bool";
    case "int16":
      return "short";
    case "int32":
      return "int";
    case "int64":
      return "long";
    case "decimal":
      return "decimal";
    case "double":
      return "double";
    case "single":
      return "float";
    case "datetime":
      return "datetime";
    case "dateonly":
      return "date";
    case "guid":
      return "guid";
    default:
      return shortName;
  }
};

const normalizeDataTypeName = (value) => normalizeString(value).toLowerCase();

const isNumericDataTypeName = (value) => {
  const normalized = normalizeDataTypeName(value);
  return normalized.includes("int")
    || normalized.includes("decimal")
    || normalized.includes("double")
    || normalized.includes("float")
    || normalized.includes("number")
    || normalized.includes("single")
    || normalized.includes("long")
    || normalized.includes("short");
};

const isComparableDataTypeName = (value) => {
  const normalized = normalizeDataTypeName(value);
  if (!normalized || normalized.includes("bool") || normalized.includes("guid")) {
    return false;
  }

  return isNumericDataTypeName(normalized)
    || normalized.includes("string")
    || normalized.includes("date")
    || normalized.includes("time");
};

const inferAggregateOutputDataType = (aggregationKind, targetDataTypeName) => {
  switch (Number(aggregationKind ?? AGGREGATION_KIND_SUM)) {
    case AGGREGATION_KIND_COUNT:
    case AGGREGATION_KIND_COUNT_DISTINCT:
      return "System.Int64";
    case AGGREGATION_KIND_AVERAGE:
    case AGGREGATION_KIND_MEDIAN:
    case AGGREGATION_KIND_STD_DEV:
    case AGGREGATION_KIND_VARIANCE:
      return "System.Decimal";
    case AGGREGATION_KIND_SUM:
      return normalizeString(targetDataTypeName) || "System.Decimal";
    case AGGREGATION_KIND_MIN:
    case AGGREGATION_KIND_MAX:
    case AGGREGATION_KIND_FIRST:
    case AGGREGATION_KIND_LAST:
      return normalizeString(targetDataTypeName) || "";
    default:
      return normalizeString(targetDataTypeName) || "";
  }
};

const isAggregationCompatible = (aggregationKind, dataTypeName) => {
  const normalizedAggregationKind = Number(aggregationKind ?? AGGREGATION_KIND_SUM);

  if (ANY_TYPE_AGGREGATION_KINDS.has(normalizedAggregationKind)) {
    return true;
  }

  if (NUMERIC_AGGREGATION_KINDS.has(normalizedAggregationKind)) {
    return isNumericDataTypeName(dataTypeName);
  }

  if (COMPARABLE_AGGREGATION_KINDS.has(normalizedAggregationKind)) {
    return isComparableDataTypeName(dataTypeName);
  }

  return false;
};

const getAggregationLabel = (aggregationKind) =>
  VISUAL_AGGREGATION_OPTIONS.find((option) => Number(option.Value) === Number(aggregationKind ?? AGGREGATION_KIND_SUM))?.Text ?? "Aggregate";

const buildVisualAggregateNames = (column, aggregationKind) => {
  const fieldName = normalizeString(column?.FieldName ?? column?.ColumnName ?? column?.Value);
  const displayName = normalizeString(column?.DisplayName) || toFriendlyLabel(fieldName);
  const aggregationLabel = getAggregationLabel(aggregationKind);
  const outputName = `${normalizeMachineToken(fieldName)}_${normalizeMachineToken(aggregationLabel)}`;

  return {
    OutputName: outputName,
    DisplayName: `${aggregationLabel} of ${displayName}`,
  };
};

export const getCompatibleVisualAggregationOptions = (column) =>
  VISUAL_AGGREGATION_OPTIONS.filter((option) => isAggregationCompatible(option.Value, column?.DataTypeName));

export const buildVisualAggregateDraft = (column, aggregationKind = null, existing = null) => {
  const compatibleOptions = getCompatibleVisualAggregationOptions(column);
  if (compatibleOptions.length === 0) {
    return null;
  }

  const normalizedAggregationKind =
    compatibleOptions.find((option) => Number(option.Value) === Number(aggregationKind))?.Value
    ?? compatibleOptions[0]?.Value
    ?? AGGREGATION_KIND_SUM;
  const generatedNames = buildVisualAggregateNames(column, normalizedAggregationKind);
  const uniqueId = normalizeString(existing?.UniqueId ?? existing?._id) || createClientId("visual-aggregate");

  return {
    ...(existing ?? {}),
    _id: existing?._id ?? uniqueId,
    UniqueId: uniqueId,
    Column: normalizeString(column?.FieldName ?? column?.ColumnName ?? column?.Value),
    AggregationKind: normalizedAggregationKind,
    OutputName: generatedNames.OutputName,
    DisplayName: generatedNames.DisplayName,
    Priority: Number.isFinite(Number(existing?.Priority)) ? Number(existing.Priority) : 0,
    Enabled: existing?.Enabled !== false,
  };
};

const FIELD_REFERENCE_KEYS = ["FieldName", "fieldName", "Name", "name", "ColumnName", "columnName", "Column", "column", "fn", "Field", "field"];

const coerceBoolean = (value, fallback = false) => {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true") {
      return true;
    }
    if (normalized === "false") {
      return false;
    }
  }

  if (typeof value === "number") {
    return value !== 0;
  }

  return fallback;
};

const isMeaningfulValue = (value) => {
  if (value === undefined || value === null) {
    return false;
  }

  if (typeof value === "string") {
    return value.trim() !== "";
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return true;
};

const parseJsonList = (value) => {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value !== "string") {
    return [];
  }

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const pickFieldValue = (field, ...keys) => {
  for (const key of keys) {
    if (field?.[key] !== undefined) {
      return field[key];
    }
  }

  return undefined;
};

const normalizeFieldType = (field) => {
  const controlType = normalizeString(field?.ControlType).toLowerCase();
  const valueSource = normalizeString(field?.ValueSource);

  if (valueSource === DESIGN_FIELD_TYPE.MODEL_COLUMNS || controlType === DESIGN_FIELD_TYPE.MODEL_COLUMN.toLowerCase()) {
    return SystemTypes.UIFieldType.SearchSelectList;
  }

  switch (controlType) {
    case DESIGN_FIELD_TYPE.BOOLEAN.toLowerCase():
      return SystemTypes.UIFieldType.CheckBox;
    case DESIGN_FIELD_TYPE.SELECT.toLowerCase():
      return SystemTypes.UIFieldType.SelectList;
    case DESIGN_FIELD_TYPE.TEXT_AREA.toLowerCase():
      return SystemTypes.UIFieldType.TextArea;
    default:
      return SystemTypes.UIFieldType.TextBox;
  }
};

const normalizeSchemaFields = (schema) =>
  asArray(schema?.Fields ?? schema?.fields)
    .filter((field) => normalizeString(pickFieldValue(field, "Key", "key")) && normalizeString(pickFieldValue(field, "Label", "label")))
    .map((field, index) => ({
      ...field,
      Key: normalizeString(pickFieldValue(field, "Key", "key")),
      Label: normalizeString(pickFieldValue(field, "Label", "label")),
      Description: pickFieldValue(field, "Description", "description"),
      ControlType: normalizeString(pickFieldValue(field, "ControlType", "controlType")) || "Text",
      ValueSource: normalizeString(pickFieldValue(field, "ValueSource", "valueSource")),
      MapsTo: normalizeString(pickFieldValue(field, "MapsTo", "mapsTo")),
      TabId: normalizeString(pickFieldValue(field, "TabId", "tabId")) || "Design|1",
      GroupId: normalizeString(pickFieldValue(field, "GroupId", "groupId")) || "Design|1",
      Sequence: Number.isFinite(Number(pickFieldValue(field, "Sequence", "sequence")))
        ? Number(pickFieldValue(field, "Sequence", "sequence"))
        : index + 1,
      Required: coerceBoolean(pickFieldValue(field, "Required", "required"), false),
      DefaultValue: pickFieldValue(field, "DefaultValue", "defaultValue"),
      DataJson: pickFieldValue(field, "DataJson", "dataJson"),
    }))
    .sort((a, b) => a.Sequence - b.Sequence);

const getCustomFields = (visualObject) => asObject(visualObject?.CustomFields);

const getCanonicalFromMapsTo = (mapsTo) => {
  const normalized = normalizeString(mapsTo);
  return normalized.startsWith("canonical:") ? normalized.slice("canonical:".length) : "";
};

const formatFieldLabel = (field) => {
  const canonical = getCanonicalFromMapsTo(field?.MapsTo).toLowerCase();
  if (canonical === "x" || canonical === "y") {
    return `${field.Label} (${canonical})`;
  }

  return field.Label;
};

const getRenderKeyFromMapsTo = (mapsTo) => {
  const normalized = normalizeString(mapsTo);
  return normalized.startsWith("render:") ? normalized.slice("render:".length) : "";
};

const findVisualColumnBinding = (visualObject, canonical) => {
  const matches = asArray(visualObject?.VisualColumns).filter(
    (column) => normalizeString(column?.Canonical).toLowerCase() === normalizeString(canonical).toLowerCase()
  );

  if (matches.length === 0) {
    return null;
  }

  return matches.find((column) => column?.IsDefaultForCanonical) ?? matches[0];
};

const readFieldValueFromVisual = (visualObject, field) => {
  const customFields = getCustomFields(visualObject);
  const customValue = customFields?.[field.Key];
  const fieldType = normalizeFieldType(field);

  if (customValue !== undefined) {
    return fieldType === SystemTypes.UIFieldType.CheckBox
      ? coerceBoolean(customValue, coerceBoolean(field?.DefaultValue))
      : customValue;
  }

  const canonical = getCanonicalFromMapsTo(field?.MapsTo);
  if (canonical) {
    const binding = findVisualColumnBinding(visualObject, canonical);
    if (binding?.FieldName) {
      return binding.FieldName;
    }
  }

  const renderKey = getRenderKeyFromMapsTo(field?.MapsTo);
  if (renderKey) {
    const renderValue = visualObject?.RenderSettings?.[renderKey];
    if (renderValue !== undefined) {
      return fieldType === SystemTypes.UIFieldType.CheckBox
        ? coerceBoolean(renderValue, coerceBoolean(field?.DefaultValue))
        : renderValue;
    }
  }

  if (fieldType === SystemTypes.UIFieldType.CheckBox) {
    return coerceBoolean(field?.DefaultValue);
  }

  return field?.DefaultValue ?? "";
};

const buildColumnOptions = (modelColumns = []) =>
  asArray(modelColumns).map((column) => ({
    Text: (() => {
      const label = column?.DisplayName || column?.ColumnName || column?.FieldName;
      const typeLabel = formatColumnDataTypeName(column?.DataTypeName);
      return typeLabel ? `${label} (${typeLabel})` : label;
    })(),
    Value: column?.ColumnName || column?.FieldName,
  })).filter((option) => isMeaningfulValue(option?.Text) && isMeaningfulValue(option?.Value));

const buildSelectOptions = (field) =>
  parseJsonList(field?.DataJson)
    .map((item) => ({
      Text: item?.Text ?? item?.Label ?? item?.Name ?? item?.FriendlyName ?? item?.Value,
      Value: item?.Value ?? item?._id ?? item?.Id ?? item?.Text,
    }))
    .filter((option) => isMeaningfulValue(option?.Text) && isMeaningfulValue(option?.Value));

const getModelColumnFieldName = (column) =>
  normalizeString(
    column?.ColumnName ??
    column?.columnName ??
    column?.FieldName ??
    column?.fieldName ??
    column?.Name ??
    column?.name
  );

const buildModelFieldLookup = (modelColumns = []) =>
  asArray(modelColumns).reduce((lookup, column) => {
    const fieldName = getModelColumnFieldName(column);
    if (!fieldName) {
      return lookup;
    }

    const key = fieldName.toLowerCase();
    if (!lookup.has(key)) {
      lookup.set(key, {
        ...column,
        FieldName: fieldName,
      });
    }

    return lookup;
  }, new Map());

const buildModelMeasureLookup = (modelMeasures = []) =>
  asArray(modelMeasures).reduce((lookup, measure) => {
    const fieldName = normalizeString(
      measure?.MeasureName ??
      measure?.FieldName ??
      measure?.fieldName ??
      measure?.Name ??
      measure?.name
    );
    if (!fieldName) {
      return lookup;
    }

    const key = fieldName.toLowerCase();
    if (!lookup.has(key)) {
      lookup.set(key, {
        ...measure,
        FieldName: fieldName,
      });
    }

    return lookup;
  }, new Map());

const resolveModelFieldName = (fieldName, fieldLookup) =>
  fieldLookup.get(normalizeString(fieldName).toLowerCase())?.FieldName ?? "";

export const hasExplicitVisualGrouping = (queryState) =>
  asArray(queryState?.GroupByColumns).length > 0
  || asArray(queryState?.MeasureNames).length > 0
  || asArray(queryState?.VisualAggregates).length > 0;

const isModelColumnField = (field) => {
  const controlType = normalizeString(field?.ControlType).toLowerCase();
  const valueSource = normalizeString(field?.ValueSource);

  return (
    valueSource === DESIGN_FIELD_TYPE.MODEL_COLUMNS ||
    controlType === DESIGN_FIELD_TYPE.MODEL_COLUMN.toLowerCase() ||
    controlType === DESIGN_FIELD_TYPE.MODEL_COLUMNS.toLowerCase()
  );
};

const sanitizeColumnSelectionValue = (value, fieldLookup) => {
  if (Array.isArray(value)) {
    return Array.from(
      new Set(
        value
          .map((entry) => resolveModelFieldName(entry, fieldLookup))
          .filter(Boolean)
      )
    );
  }

  return resolveModelFieldName(value, fieldLookup);
};

const sanitizeCustomFields = (customFields, schemaFields, fieldLookup, shouldSanitizeColumns) => {
  const nextCustomFields = {
    ...asObject(customFields),
  };

  if (!shouldSanitizeColumns || schemaFields.length === 0) {
    return nextCustomFields;
  }

  schemaFields.forEach((field) => {
    if (!isModelColumnField(field)) {
      return;
    }

    const currentValue = nextCustomFields[field.Key];
    if (currentValue === undefined) {
      return;
    }

    const sanitizedValue = sanitizeColumnSelectionValue(currentValue, fieldLookup);
    if (Array.isArray(currentValue)) {
      if (sanitizedValue.length > 0) {
        nextCustomFields[field.Key] = sanitizedValue;
        return;
      }
      nextCustomFields[field.Key] = [];
      return;
    } else if (sanitizedValue) {
      nextCustomFields[field.Key] = sanitizedValue;
      return;
    }

    // Preserve an explicit empty override so the designer does not fall back
    // to the prior VisualColumns binding when an optional field is cleared.
    nextCustomFields[field.Key] = "";
  });

  return nextCustomFields;
};

const sanitizeVisualColumns = (visualColumns, fieldLookup, shouldSanitizeColumns) => {
  if (!shouldSanitizeColumns) {
    return deepCopy(asArray(visualColumns));
  }

  const seen = new Set();

  return asArray(visualColumns).reduce((nextColumns, column) => {
    const actualFieldName = resolveModelFieldName(column?.FieldName, fieldLookup);
    if (!actualFieldName) {
      return nextColumns;
    }

    const dedupeKey = `${normalizeString(column?.Canonical).toLowerCase()}::${actualFieldName.toLowerCase()}::${column?.IsDefaultForCanonical ? "1" : "0"}`;
    if (seen.has(dedupeKey)) {
      return nextColumns;
    }

    seen.add(dedupeKey);
    nextColumns.push({
      ...column,
      FieldName: actualFieldName,
    });
    return nextColumns;
  }, []);
};

const sanitizeColumnNameList = (columns, fieldLookup, shouldSanitizeColumns) => {
  if (!shouldSanitizeColumns) {
    return [...asArray(columns)];
  }

  return Array.from(
    new Set(
      asArray(columns)
        .map((fieldName) => resolveModelFieldName(fieldName, fieldLookup))
        .filter(Boolean)
    )
  );
};

const sanitizeFieldReferenceObjectList = (items, fieldLookup, shouldSanitizeColumns) => {
  if (!shouldSanitizeColumns) {
    return deepCopy(asArray(items));
  }

  return asArray(items).reduce((nextItems, item) => {
    if (typeof item === "string") {
      const actualFieldName = resolveModelFieldName(item, fieldLookup);
      if (actualFieldName) {
        nextItems.push(actualFieldName);
      }
      return nextItems;
    }

    if (!item || typeof item !== "object" || Array.isArray(item)) {
      nextItems.push(item);
      return nextItems;
    }

    const fieldKey = FIELD_REFERENCE_KEYS.find((key) => item[key] !== undefined);
    if (!fieldKey) {
      nextItems.push(deepCopy(item));
      return nextItems;
    }

    const actualFieldName = resolveModelFieldName(item[fieldKey], fieldLookup);
    if (!actualFieldName) {
      return nextItems;
    }

    nextItems.push({
      ...item,
      [fieldKey]: actualFieldName,
    });
    return nextItems;
  }, []);
};

const sanitizeVisualAggregates = (items, fieldLookup, shouldSanitizeColumns) => {
  if (!shouldSanitizeColumns) {
    return deepCopy(asArray(items));
  }

  const seen = new Set();

  return asArray(items).reduce((nextItems, item, index) => {
    const sourceColumn = resolveModelFieldName(item?.Column, fieldLookup);
    if (!sourceColumn) {
      return nextItems;
    }

    const sourceField = fieldLookup.get(sourceColumn.toLowerCase());
    const compatibleOptions = VISUAL_AGGREGATION_OPTIONS.filter((option) =>
      isAggregationCompatible(option.Value, sourceField?.DataTypeName)
    );
    if (compatibleOptions.length === 0) {
      return nextItems;
    }

    const requestedAggregationKind = Number(item?.AggregationKind ?? AGGREGATION_KIND_SUM);
    const normalizedAggregationKind =
      compatibleOptions.find((option) => Number(option.Value) === requestedAggregationKind)?.Value
      ?? compatibleOptions[0]?.Value
      ?? AGGREGATION_KIND_SUM;
    const dedupeKey = `${sourceColumn.toLowerCase()}::${normalizedAggregationKind}`;

    if (seen.has(dedupeKey)) {
      return nextItems;
    }

    seen.add(dedupeKey);
    const generatedNames = buildVisualAggregateNames(sourceField, normalizedAggregationKind);
    const uniqueId = normalizeString(item?.UniqueId ?? item?._id) || createClientId("visual-aggregate");

    nextItems.push({
      ...item,
      _id: item?._id ?? uniqueId,
      UniqueId: uniqueId,
      Column: sourceColumn,
      AggregationKind: normalizedAggregationKind,
      OutputName: generatedNames.OutputName,
      DisplayName: generatedNames.DisplayName,
      Priority: Number.isFinite(Number(item?.Priority)) ? Number(item.Priority) : index,
      Enabled: item?.Enabled !== false,
    });
    return nextItems;
  }, []);
};

const buildVisualAggregateOutputField = (aggregate, fieldLookup) => {
  const sourceColumn = resolveModelFieldName(aggregate?.Column, fieldLookup);
  if (!sourceColumn) {
    return null;
  }

  const sourceField = fieldLookup.get(sourceColumn.toLowerCase());
  if (!sourceField) {
    return null;
  }

  const generatedNames = buildVisualAggregateNames(sourceField, aggregate?.AggregationKind);

  return {
    FieldName: generatedNames.OutputName,
    ColumnName: generatedNames.OutputName,
    SourceFieldName: sourceColumn,
    DisplayName: generatedNames.DisplayName,
    DataTypeName: inferAggregateOutputDataType(aggregate?.AggregationKind, sourceField?.DataTypeName),
    Format: sourceField?.Format ?? "",
    SemanticType: "measure",
    Aggregation: normalizeMachineToken(getAggregationLabel(aggregate?.AggregationKind)),
    BindingKind: "visualaggregate",
    CanFilter: false,
    CanGroup: false,
    CanSort: true,
    IsVisible: aggregate?.Enabled !== false,
  };
};

export const buildVisualQueryOutputFields = (queryState, modelColumns = [], modelMeasures = []) => {
  if (!hasExplicitVisualGrouping(queryState)) {
    return [];
  }

  const fieldLookup = buildModelFieldLookup(modelColumns);
  const measureLookup = buildModelMeasureLookup(modelMeasures);
  const nextFields = [];
  const seen = new Set();

  const pushField = (field) => {
    const fieldName = normalizeString(field?.FieldName ?? field?.ColumnName);
    if (!fieldName) {
      return;
    }

    const dedupeKey = fieldName.toLowerCase();
    if (seen.has(dedupeKey)) {
      return;
    }

    seen.add(dedupeKey);
    nextFields.push({
      ...field,
      FieldName: fieldName,
      ColumnName: normalizeString(field?.ColumnName) || fieldName,
      DisplayName: normalizeString(field?.DisplayName) || fieldName,
    });
  };

  asArray(queryState?.GroupByColumns).forEach((fieldName) => {
    const sourceField = fieldLookup.get(normalizeString(fieldName).toLowerCase());
    if (!sourceField) {
      return;
    }

    pushField({
      ...sourceField,
      FieldName: sourceField.FieldName,
      ColumnName: sourceField.ColumnName ?? sourceField.FieldName,
      BindingKind: sourceField.BindingKind ?? "column",
      Aggregation: "none",
      CanGroup: sourceField.CanGroup !== false,
      CanFilter: sourceField.CanFilter !== false,
      CanSort: sourceField.CanSort !== false,
    });
  });

  asArray(queryState?.MeasureNames).forEach((fieldName) => {
    const sourceMeasure = measureLookup.get(normalizeString(fieldName).toLowerCase());
    if (!sourceMeasure) {
      return;
    }

    pushField({
      ...sourceMeasure,
      FieldName: sourceMeasure.FieldName,
      ColumnName: sourceMeasure.FieldName,
      DisplayName: normalizeString(sourceMeasure?.DisplayName) || sourceMeasure.FieldName,
      BindingKind: "measure",
      SemanticType: "measure",
      Aggregation: normalizeMachineToken(sourceMeasure?.Aggregation ?? sourceMeasure?.AggregationKind ?? "measure"),
      CanFilter: false,
      CanGroup: false,
      CanSort: true,
    });
  });

  sanitizeVisualAggregates(queryState?.VisualAggregates, fieldLookup, true).forEach((aggregate) => {
    const aggregateField = buildVisualAggregateOutputField(aggregate, fieldLookup);
    if (aggregateField) {
      pushField(aggregateField);
    }
  });

  return nextFields;
};

const getActiveVisualBindings = (visualColumns = []) => {
  const byCanonical = new Map();

  asArray(visualColumns).forEach((column, index) => {
    const canonical = normalizeString(column?.Canonical);
    const fieldName = normalizeString(column?.FieldName);
    if (!canonical || canonical.toLowerCase() === "none" || !fieldName) {
      return;
    }

    const existing = byCanonical.get(canonical.toLowerCase());
    if (!existing || column?.IsDefaultForCanonical) {
      byCanonical.set(canonical.toLowerCase(), {
        ...column,
        _bindingOrder: index,
      });
    }
  });

  return Array.from(byCanonical.values()).sort((a, b) => Number(a?._bindingOrder ?? 0) - Number(b?._bindingOrder ?? 0));
};

const deriveQueryStateFromVisualBindings = (queryState, visualObject, fieldLookup, shouldSanitizeColumns) => {
  const activeBindings = getActiveVisualBindings(visualObject?.VisualColumns);
  const nextVisibleColumns = Array.from(
    new Set(
      activeBindings
        .map((binding) => shouldSanitizeColumns
          ? resolveModelFieldName(binding?.FieldName, fieldLookup)
          : normalizeString(binding?.FieldName))
        .filter(Boolean)
    )
  );

  return {
    ...queryState,
    VisibleColumns: nextVisibleColumns,
  };
};

const sanitizeFilterDraftAgainstColumns = (filterDraft, fieldLookup, shouldSanitizeColumns) => {
  if (!shouldSanitizeColumns) {
    return deepCopy(asArray(filterDraft));
  }

  return asArray(filterDraft)
    .map((group) =>
      asArray(group)
        .map((row) => {
          const actualFieldName = resolveModelFieldName(row?.fn ?? row?.FieldName ?? row?.field, fieldLookup);
          if (!actualFieldName) {
            return null;
          }

          return {
            ...row,
            fn: actualFieldName,
            ...(row?.FieldName !== undefined ? { FieldName: actualFieldName } : {}),
            ...(row?.field !== undefined ? { field: actualFieldName } : {}),
          };
        })
        .filter(Boolean)
    )
    .filter((group) => group.length > 0);
};

const sanitizeVisualQueryState = (queryState, visualObject, fieldLookup, shouldSanitizeColumns) => {
  const baseState = normalizeDashboardQueryState(queryState, {
    ModelKind: Number(visualObject?.ModelKind ?? 0),
    ModelId: visualObject?.ModelId ?? "",
  });
  const derivedState = deriveQueryStateFromVisualBindings(baseState, visualObject, fieldLookup, shouldSanitizeColumns);
  const supportsRealTime = !!derivedState?.SupportsRealTime;
  const sanitizedGroupByColumns = sanitizeColumnNameList(derivedState?.GroupByColumns, fieldLookup, shouldSanitizeColumns);
  const groupedColumnKeys = new Set(
    sanitizedGroupByColumns
      .map((value) => normalizeString(value).toLowerCase())
      .filter(Boolean)
  );
  const sanitizedVisualAggregates = sanitizeVisualAggregates(derivedState?.VisualAggregates, fieldLookup, shouldSanitizeColumns)
    .filter((aggregate) => !groupedColumnKeys.has(normalizeString(aggregate?.Column).toLowerCase()))
    .map((aggregate, index) => ({
      ...aggregate,
      Priority: Number.isFinite(Number(aggregate?.Priority)) ? Number(aggregate.Priority) : index,
    }));
  const hasSemanticGrouping = (
    !supportsRealTime && (
      sanitizedGroupByColumns.length > 0
      || (Array.isArray(derivedState?.MeasureNames)
        ? [...derivedState.MeasureNames].map((value) => normalizeString(value)).filter(Boolean)
        : []).length > 0
      || sanitizedVisualAggregates.length > 0
    )
  );

  return normalizeDashboardQueryState(derivedState, {
    ModelKind: Number(visualObject?.ModelKind ?? 0),
    ModelId: visualObject?.ModelId ?? "",
    QueryMode: hasSemanticGrouping ? 2 : Number(derivedState?.QueryMode ?? queryState?.QueryMode ?? 1),
    SemanticShape: hasSemanticGrouping ? 2 : Number(derivedState?.SemanticShape ?? queryState?.SemanticShape ?? 1),
    SelectColumns: sanitizeColumnNameList(derivedState?.SelectColumns, fieldLookup, shouldSanitizeColumns),
    VisibleColumns: sanitizeColumnNameList(derivedState?.VisibleColumns, fieldLookup, shouldSanitizeColumns),
    GroupByColumns: sanitizedGroupByColumns,
    MeasureNames: Array.isArray(derivedState?.MeasureNames)
      ? [...derivedState.MeasureNames].map((value) => normalizeString(value)).filter(Boolean)
      : [],
    VisualAggregates: sanitizedVisualAggregates,
    Sorts: sanitizeFieldReferenceObjectList(derivedState?.Sorts, fieldLookup, shouldSanitizeColumns),
    Groupings: sanitizeFieldReferenceObjectList(derivedState?.Groupings, fieldLookup, shouldSanitizeColumns),
    FilterDraft: sanitizeFilterDraftAgainstColumns(derivedState?.FilterDraft, fieldLookup, shouldSanitizeColumns),
  });
};

export const parseVisualDesignSchema = (visualType, visualObject = null) => {
  const visualTypeKey = normalizeString(visualType?.ShortName || visualType?.Type).toLowerCase();
  const rawGeneratedDefinition =
    visualTypeKey === "custom"
      ? visualObject?.CustomEChartsDefinitionJson
      : visualTypeKey === "table"
        ? visualObject?.TableViewDefinitionJson
        : visualTypeKey === "matrix"
          ? visualObject?.MatrixDefinitionJson
      : visualTypeKey === "airenderhtml"
        ? visualObject?.AIRenderHtmlDefinitionJson
        : visualTypeKey === "htmlcard"
          ? visualObject?.HtmlCardDefinitionJson
        : null;
  let generatedDefinition = null;

  if (typeof rawGeneratedDefinition === "object" && rawGeneratedDefinition) {
    generatedDefinition = rawGeneratedDefinition;
  } else if (typeof rawGeneratedDefinition === "string" && rawGeneratedDefinition.trim()) {
    try {
      generatedDefinition = JSON.parse(rawGeneratedDefinition);
    } catch {
      generatedDefinition = null;
    }
  }

  const rawSchema = generatedDefinition?.ManualBindingSchemaJson ?? visualType?.ManualBindingSchemaJson;
  if (!rawSchema) {
    return null;
  }

  if (typeof rawSchema === "object") {
    return rawSchema;
  }

  try {
    return JSON.parse(rawSchema);
  } catch {
    return null;
  }
};

export const buildVisualDesignMetadata = (schema, modelColumns = []) => {
  const schemaFields = normalizeSchemaFields(schema);
  const modelColumnOptions = buildColumnOptions(modelColumns);

  return {
    Name: "VisualDesign",
    Fields: schemaFields.map((field) => {
      const fieldType = normalizeFieldType(field);
      const displayLabel = formatFieldLabel(field);
      return {
        FieldName: field.Key,
        DisplayName: displayLabel,
        Description: field.Description ?? displayLabel,
        FieldType: fieldType,
        DataJson:
          field.ValueSource === DESIGN_FIELD_TYPE.MODEL_COLUMNS || normalizeString(field.ControlType).toLowerCase() === DESIGN_FIELD_TYPE.MODEL_COLUMN.toLowerCase()
            ? modelColumnOptions
            : buildSelectOptions(field),
        IsRequired: field.Required,
        DisplaySequence: field.Sequence,
        GridSequence: field.Sequence,
        TabId: field.TabId,
        GroupId: field.GroupId,
        DefaultSelection: field.DefaultValue,
        PlaceHolder: fieldType === SystemTypes.UIFieldType.SearchSelectList || fieldType === SystemTypes.UIFieldType.SelectList
          ? "Select..."
          : undefined,
        CustomFields: {
          VisualBindingMapsTo: field.MapsTo ?? "",
          VisualBindingControlType: field.ControlType,
          VisualBindingValueSource: field.ValueSource ?? "",
        },
      };
    }),
  };
};

export const buildVisualDesignItem = (visualObject, schema) => {
  const schemaFields = normalizeSchemaFields(schema);
  return schemaFields.reduce((result, field) => {
    result[field.Key] = readFieldValueFromVisual(visualObject, field);
    return result;
  }, {});
};

const deriveSemanticType = (column) => {
  const semanticType = normalizeString(column?.SemanticType).toLowerCase();
  if (semanticType && semanticType !== "none") {
    return semanticType;
  }

  const dataTypeName = normalizeString(column?.DataTypeName).toLowerCase();
  const fieldName = normalizeString(column?.FieldName).toLowerCase();

  if (dataTypeName.includes("date") || dataTypeName.includes("time")) {
    return "time";
  }

  if (fieldName.endsWith("id") || fieldName.includes("_id")) {
    return "id";
  }

  if (dataTypeName.includes("int") || dataTypeName.includes("decimal") || dataTypeName.includes("double") || dataTypeName.includes("float") || dataTypeName.includes("number")) {
    return "measure";
  }

  return "dimension";
};

const deriveAggregation = (column, semanticType) => {
  const existingAggregation = normalizeString(column?.Aggregation).toLowerCase();
  if (existingAggregation) {
    return existingAggregation;
  }

  return semanticType === "measure" ? "sum" : "none";
};

const deriveGeoKind = (fieldName = "") => {
  const normalized = normalizeString(fieldName).toLowerCase();
  if (normalized.includes("lat")) {
    return "lat";
  }
  if (normalized.includes("lng") || normalized.includes("lon") || normalized.includes("long")) {
    return "lon";
  }
  if (normalized.endsWith("id")) {
    return "id";
  }
  return "none";
};

const compileVisualColumns = (visualObject, schema, modelColumns = []) => {
  const schemaFields = normalizeSchemaFields(schema);
  const controlledCanonicals = schemaFields
    .map((field) => getCanonicalFromMapsTo(field.MapsTo))
    .filter(Boolean);

  const existingColumns = asArray(visualObject?.VisualColumns);
  const preservedColumns = existingColumns.filter(
    (column) => !controlledCanonicals.includes(normalizeString(column?.Canonical))
  );

  const compiledColumns = schemaFields
    .map((field) => {
      const canonical = getCanonicalFromMapsTo(field.MapsTo);
      if (!canonical) {
        return null;
      }

      const fieldName = normalizeString(readFieldValueFromVisual(visualObject, field));
      if (!fieldName) {
        return null;
      }

      const existing = existingColumns.find(
        (column) => normalizeString(column?.Canonical).toLowerCase() === canonical.toLowerCase()
      );
      const sourceColumn = asArray(modelColumns).find(
        (column) => normalizeString(column?.FieldName).toLowerCase() === fieldName.toLowerCase()
      );
      const semanticType = deriveSemanticType(sourceColumn);

      return {
        ...(existing ?? {}),
        _id: existing?._id ?? createClientId("visual-column"),
        UniqueId: existing?.UniqueId ?? createClientId("visual-column"),
        IsActive: existing?.IsActive ?? true,
        IsDeleted: existing?.IsDeleted ?? false,
        FieldName: fieldName,
        BindingKind: existing?.BindingKind ?? sourceColumn?.BindingKind ?? "column",
        Canonical: canonical,
        SemanticType: existing?.SemanticType ?? semanticType,
        Aggregation: existing?.Aggregation ?? deriveAggregation(sourceColumn, semanticType),
        TimeGrain: existing?.TimeGrain ?? "none",
        GeoKind: existing?.GeoKind ?? deriveGeoKind(fieldName),
        IsDefaultForCanonical: true,
      };
    })
    .filter(Boolean);

  return [...preservedColumns, ...compiledColumns];
};

const seedVisualColumnsFromModelColumns = (visualColumns = [], modelColumns = []) => {
  const nextColumns = deepCopy(asArray(visualColumns));
  const existingByFieldName = new Map(
    nextColumns
      .map((column) => [normalizeString(column?.FieldName).toLowerCase(), column])
      .filter(([key]) => !!key)
  );

  asArray(modelColumns).forEach((sourceColumn) => {
    const fieldName = normalizeString(sourceColumn?.FieldName);
    if (!fieldName) {
      return;
    }

    const existing = existingByFieldName.get(fieldName.toLowerCase()) ?? null;
    const semanticType = deriveSemanticType(sourceColumn);
    const seededColumn = {
      ...(existing ?? {}),
      _id: existing?._id ?? createClientId("visual-column"),
      UniqueId: existing?.UniqueId ?? createClientId("visual-column"),
      IsActive: existing?.IsActive ?? true,
      IsDeleted: existing?.IsDeleted ?? false,
      FieldName: fieldName,
      BindingKind: existing?.BindingKind ?? sourceColumn?.BindingKind ?? "column",
      Canonical: existing?.Canonical ?? "none",
      SemanticType: existing?.SemanticType ?? semanticType,
      Aggregation: existing?.Aggregation ?? deriveAggregation(sourceColumn, semanticType),
      TimeGrain: existing?.TimeGrain ?? "none",
      GeoKind: existing?.GeoKind ?? deriveGeoKind(fieldName),
      IsDefaultForCanonical: existing?.IsDefaultForCanonical ?? false,
    };

    if (existing) {
      Object.assign(existing, seededColumn);
      return;
    }

    nextColumns.push(seededColumn);
    existingByFieldName.set(fieldName.toLowerCase(), seededColumn);
  });

  return nextColumns;
};

const compileRenderSettings = (visualObject, schema) => {
  const schemaFields = normalizeSchemaFields(schema);
  const nextRenderSettings = {
    ...(visualObject?.RenderSettings ?? {}),
  };

  const controlledKeys = schemaFields
    .map((field) => getRenderKeyFromMapsTo(field.MapsTo))
    .filter(Boolean);

  controlledKeys.forEach((key) => {
    delete nextRenderSettings[key];
  });

  schemaFields.forEach((field) => {
    const renderKey = getRenderKeyFromMapsTo(field.MapsTo);
    if (!renderKey) {
      return;
    }

    const fieldType = normalizeFieldType(field);
    const value = readFieldValueFromVisual(visualObject, field);

    if (fieldType === SystemTypes.UIFieldType.CheckBox) {
      nextRenderSettings[renderKey] = coerceBoolean(value) ? "true" : "false";
      return;
    }

    if (isMeaningfulValue(value)) {
      nextRenderSettings[renderKey] = String(value);
    }
  });

  return nextRenderSettings;
};

export const applyVisualDesignSchema = (visualObject, schema, bindingColumns = []) => {
  if (!visualObject || !schema) {
    return visualObject;
  }

  const schemaFields = normalizeSchemaFields(schema);
  const fieldLookup = buildModelFieldLookup(bindingColumns);
  const shouldSanitizeColumns = fieldLookup.size > 0;
  const nextVisual = {
    ...visualObject,
    CustomFields: sanitizeCustomFields(visualObject?.CustomFields, schemaFields, fieldLookup, shouldSanitizeColumns),
  };

  return {
    ...nextVisual,
    VisualColumns: compileVisualColumns(nextVisual, schema, bindingColumns),
    RenderSettings: compileRenderSettings(nextVisual, schema),
  };
};

export const sanitizeVisualConfiguration = (visualObject, schema = null, modelColumns = [], bindingColumns = modelColumns) => {
  if (!visualObject) {
    return visualObject;
  }

  const schemaFields = normalizeSchemaFields(schema);
  const fieldLookup = buildModelFieldLookup(modelColumns);
  const bindingFieldLookup = buildModelFieldLookup(bindingColumns);
  const shouldSanitizeColumns = fieldLookup.size > 0;
  const shouldSanitizeBindingColumns = bindingFieldLookup.size > 0;

  let nextVisual = {
    ...visualObject,
    CustomFields: sanitizeCustomFields(visualObject?.CustomFields, schemaFields, bindingFieldLookup, shouldSanitizeBindingColumns),
  };

  if (schema) {
    nextVisual = applyVisualDesignSchema(nextVisual, schema, bindingColumns);
  }

  const seededVisualColumns = seedVisualColumnsFromModelColumns(nextVisual?.VisualColumns, modelColumns);

  return {
    ...nextVisual,
    VisualColumns: sanitizeVisualColumns(seededVisualColumns, fieldLookup, shouldSanitizeColumns),
    DefaultQueryState: sanitizeVisualQueryState(nextVisual?.DefaultQueryState, nextVisual, fieldLookup, shouldSanitizeColumns),
  };
};

export const updateVisualDesignField = (visualObject, schema, modelColumns, fieldName, value, bindingColumns = modelColumns) => {
  if (!visualObject) {
    return visualObject;
  }

  const nextVisual = {
    ...visualObject,
    CustomFields: {
      ...(visualObject?.CustomFields ?? {}),
      [fieldName]: value,
    },
  };

  return sanitizeVisualConfiguration(nextVisual, schema, modelColumns, bindingColumns);
};
